<template>
  <div class="search__container">
    <div class="search__bar">
      <div class="search__bar--content">
        <label class="search__bar--label" for="search_input">
          <Icon icon="search" />
        </label>
        <input
          id="search_input"
          v-model="message"
          aria-disabled="true"
          class="search__bar--input p4"
          placeholder="Search Brooklinen"
        />
        <button
          type="button"
          class="search__bar--close"
          aria-label="Close search"
          @click="closeSearch()"
        >
          <Icon icon="close" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import Icon from '../Global/Icon.vue';
import {
  getFirstFocusableElement,
  getLastFocusableElement,
} from '@utils/global-functions';
import { captureUserActionError, USER_ACTIONS } from '@utils/sentry';

const predictiveSearch = document.querySelector('#predictive-search-dialog');
const shopifyRoutes = window.Shopify.routes.root;
const message = ref('');

const clearSearchResults = () => {
  document.querySelector('.js-search-bar-results').classList.add('hidden');
  document.querySelector('.js-search-bar-results').innerHTML = '';
  document.querySelector('#search_input').value = '';
};

const closeSearch = () => {
  clearSearchResults();
  predictiveSearch.toggle();
};

const performSearch = async (text) => {
  await fetchSearch(text, true);
  fetchSearch(text);
};

const fetchSearch = async (text, productLoad = false) => {
  const queryParams = productLoad
    ? `&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title`
    : '';
  const url = `${shopifyRoutes}search/suggest?q=${text}${queryParams}&resources[limit]=4&section_id=predictive-search`;

  try {
    const response = await fetch(url),
      json = await response.text(),
      parsedText = new DOMParser().parseFromString(json, 'text/html'),
      results = parsedText.querySelector('#shopify-section-predictive-search');

    const searchListProducts = results.querySelector('.search__list--product'),
      searchListViewAll = results.querySelector('.search__list--view-all');

    const searchResultsEl = document.querySelector('.js-search-bar-results'),
      searchListEl = searchResultsEl.querySelectorAll('.search__list--results'),
      searchListLength = searchListEl.length;

    if (productLoad && searchListLength == 0)
      searchResultsEl.innerHTML = results.innerHTML;
    else {
      // remove duplicate product and view all results
      if (searchListProducts)
        searchListProducts.parentNode.removeChild(searchListProducts);
      if (searchListViewAll)
        searchListViewAll.parentNode.removeChild(searchListViewAll);

      // prepend new view all element
      const searchListResults = results.querySelector('.Search__List--Results');
      if (
        !!searchListResults &&
        (searchListLength == 2 || searchListLength == 1)
      ) {
        const newLink = `${shopifyRoutes}search?q=${text}`;
        searchResultsEl.querySelector('.search__list--view-all').href = newLink;
        searchResultsEl
          .querySelector('.search__list--view-all')
          .before(searchListResults);
      }

      searchResultsEl.classList.remove('hidden');
    }

    return productLoad;
  } catch (error) {
    captureUserActionError(USER_ACTIONS.PREDICTIVE_SEARCH, error);
    throw error;
  }
};

/**
 * Handle keyboard interaction while the dialog is open: Escape to dismiss,
 * Enter to submit/close, and Tab to keep focus trapped inside the dialog. The
 * listener lives on `document` for the life of the page, so it must no-op while
 * the dialog is hidden.
 * @param {KeyboardEvent} e
 */
const handleKeydown = (e) => {
  if (predictiveSearch.classList.contains('hidden')) return;

  if (e.key === 'Escape') {
    closeSearch();
    return;
  }

  if (e.key === 'Enter') {
    if (e.target.classList.contains('search__bar--close')) {
      e.preventDefault();
      closeSearch();
    } else if (message.value.length > 2) {
      location.assign(`/search?q=${message.value}`);
    }
    return;
  }

  if (e.key === 'Tab') {
    // Re-query each press: result links mount only after the user types, so the
    // first/last focusable elements change over the dialog's lifetime.
    const first = getFirstFocusableElement(predictiveSearch);
    const last = getLastFocusableElement(predictiveSearch);
    if (!first || !last) return;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
};

watch(message, (text) => {
  clearSearchResults();
  if (text.length > 2) performSearch(text);
}),
  onMounted(() => {
    document.querySelector('.search__bar--input').focus();

    document.addEventListener('keydown', handleKeydown);

    document
      .querySelector('.predictive-search--overlay')
      .addEventListener('click', () => {
        closeSearch();
      });
  });
</script>
